import { ReactNode, createContext, useState, useMemo, useContext } from 'react';

type FileSet = {
  name: string;
  file?: File;
  error?: string;
  isUploading?: boolean;
  xhrObject?: XMLHttpRequest;
};

type FileSetData = { [key: string]: FileSet };

type FileDropperContextType = {
  files: FileSetData;
  setFiles: (files: FileSetData) => void;
};

const noop = () => {};

export const FileDropperContext = createContext<FileDropperContextType>({
  files: {},
  setFiles: noop,
});

export const FileDropperContextProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const [files, setFiles] = useState<FileSetData>({});

  const value = useMemo(
    () => ({
      files,
      setFiles,
    }),
    [files, setFiles]
  );

  return (
    <FileDropperContext.Provider value={value}>
      {children}
    </FileDropperContext.Provider>
  );
};

export const useFileDropperContext = (name: string) => {
  const { files, setFiles } = useContext(FileDropperContext);
  return {
    file: files[name]?.file,
    error: files[name]?.error,
    files,
    isUploading: files[name]?.isUploading,
    xhrObject: files[name]?.xhrObject,
    setFile: (file: File | undefined) => {
      const f = files[name];
      if (f) {
        f.file = file;
      } else {
        files[name] = {
          name: name,
          file: file,
        };
      }
      setFiles({ ...files });
    },
    clearFile: () => {
      delete files[name];
      setFiles({ ...files });
    },
    setXhrObject: (xhrObject: XMLHttpRequest | undefined) => {
      const f = files[name];
      if (f) {
        f.xhrObject = xhrObject;
        setFiles({ ...files });
      }
    },
    setIsUploading: (isUploading: boolean) => {
      const f = files[name];
      if (f) {
        f.isUploading = isUploading;
        setFiles({ ...files });
      }
    },
    setError: (error: string | undefined) => {
      const f = files[name];
      if (f) {
        f.error = error;
        setFiles({ ...files });
      }
    },
  };
};
